import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquarePlus, X } from 'lucide-react';
import { useUser } from 'util/UserContext';
import { Drawer, DrawerContent, DrawerTrigger } from 'ui/Drawer';
import AddFeedback from 'components/Feedback/AddFeedback';
import { storage } from 'util/storage';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { LAYER_TOKENS } from 'ui/layers';

const FloatingFeedbackButton: React.FC = () => {
  const { userIsMobile, currentUser } = useUser();
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const navigate = useNavigate();
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    // Only show if user is logged in
    if (!currentUser) {
      setIsVisible(false);
      return;
    }

    // Check if the button should be visible based on localStorage (with TTL)
    const isDismissed = storage.getJSONWithTTL<boolean>("feedbackButtonDismissed", false);
    
    if (!isDismissed) {
      setIsVisible(true);
    }
  }, [currentUser]);

  const handleDismiss = () => {
    setIsVisible(false);
    // Store in localStorage with 24-hour TTL
    storage.setJSONWithTTL("feedbackButtonDismissed", true, 24 * 60 * 60 * 1000);
  };

  const handleFeedbackSubmitted = (feedbackId: number) => {
    // Close the drawer
    setIsDrawerOpen(false);
    
    // Show success toast with link to view feedback
    toast.success('Thanks for your feedback!', {
      description: 'Your feedback helps us improve the platform.',
      action: {
        label: 'View feedback',
        onClick: () => navigate(`/feedback/${feedbackId}`)
      },
      duration: 5000
    });
  };

  // Don't render if not visible
  if (!isVisible || !isMounted) {
    return null;
  }

  return createPortal(
    <div 
      className="fixed bottom-2 sm:bottom-4 right-4 sm:right-6"
      style={{ zIndex: LAYER_TOKENS.floatingAction }}
      data-floating-feedback
    >
      <div className="relative">
        {/* Dismiss Button - positioned at top right of feedback button */}
        <button
          onClick={handleDismiss}
          className="absolute -top-2 -right-2.5 size-5 rounded-full bg-eoe-dark-icon-hover dark:bg-eoe-bg-modal shadow-lg hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover transition-colors duration-200 z-10"
          aria-label="Dismiss feedback button"
        >
          <X className="size-3 mx-auto dark:text-eoe-text-sub text-eoe-dark-text-sub" />
        </button>

        {/* Feedback Button */}
      <Drawer 
        open={isDrawerOpen} 
        onOpenChange={setIsDrawerOpen}
        direction={userIsMobile ? "bottom" : "right"}
      >
        <DrawerTrigger asChild>
          <button
            className={`rounded-full bg-eoe-entity-feedback shadow-lg transition-all duration-200 hover:scale-105 flex items-center justify-center group h-6 p-2 gap-1`}
            aria-label="Add feedback"
          >
            <MessageSquarePlus className="size-4 text-white group-hover:scale-110 transition-transform duration-200" />
            
              <span className="text-white text-xs">Feedback</span>
          </button>
        </DrawerTrigger>
        <DrawerContent
          title="Add Feedback"
          layerBase={LAYER_TOKENS.floatingSurface}
          className={!userIsMobile ? "max-w-4xl mx-auto" : ""}
        >
          <AddFeedback 
            open={true} 
            onClose={() => setIsDrawerOpen(false)}
            onFeedbackSubmitted={handleFeedbackSubmitted}
            drawerMode={true}
          />
        </DrawerContent>
      </Drawer>
      </div>
    </div>,
    document.body
  );
};

export default FloatingFeedbackButton; 